import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { mdiBankOutline, mdiCheckCircle } from "@mdi/js";
import Icon from "@mdi/react";

import Button from "components/Button";
import ChunkUploadFile from "components/ChunkUploadFile";
import PlaidLinkComponent from "components/PlaidLink";
import { NEW_CARRIER_CATEGORY } from "constants/base";
import { routes } from "constants/routes";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { linkPlaidAccountAPI } from "services/api/userSetting";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import styles from "./style.module.scss";

type TargetType = "payment_profile" | "factoring_client";

const VALID_TARGETS: TargetType[] = ["payment_profile", "factoring_client"];

export default function BankUpdate() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { target } = useParams<{ target: string }>();
  const { userToken } = useSelector((state: RootState) => state.authState);
  const { userDetails } = useSelector((state: RootState) => state.userState);

  const [isUpdating, setIsUpdating] = useState(false);
  const [bankUpdated, setBankUpdated] = useState(false);

  const isPaymentProfile = target === "payment_profile";

  const uploadUrl = useMemo(() => {
    if (isPaymentProfile && userDetails?.payment_profile_id) {
      return `upload/payment_profile_attachment/payment_profile/${userDetails.payment_profile_id}/file`;
    }
    if (!isPaymentProfile && userDetails?.factoring_id) {
      return `upload/factoring_attachment/factoring/${userDetails.factoring_id}/file`;
    }
    return "";
  }, [isPaymentProfile, userDetails?.payment_profile_id, userDetails?.factoring_id]);

  useEffect(() => {
    if (!target || !VALID_TARGETS.includes(target as TargetType)) {
      navigate("/404", { replace: true });
      return;
    }

    if (!userToken) {
      navigate(`${routes.SIGNIN}?next=/bank/${target}`, { replace: true });
    }
  }, [target, userToken, navigate]);

  const handlePlaidSuccess = async (publicToken: string, accounts: any[]) => {
    setIsUpdating(true);
    dispatch(handleLoading(true));

    try {
      const requestBody = {
        public_token: publicToken,
        account_ids: accounts.map((account) => account.id)
      };

      await linkPlaidAccountAPI(requestBody);
      showSuccessMessage("Bank account updated successfully!");
      setBankUpdated(true);
    } catch (error: any) {
      showErrorMessage(error?.message || "Failed to update bank account. Please try again.");
    } finally {
      setIsUpdating(false);
      dispatch(handleLoading(false));
    }
  };

  const handlePlaidFailed = () => {
    showErrorMessage("Bank account linking was cancelled.");
  };

  const handleUploadFinished = () => {
    showSuccessMessage("Voided check uploaded successfully!");
    setBankUpdated(true);
  };

  if (bankUpdated) {
    return (
      <div className={styles.container}>
        <div className={styles.successCard}>
          <Icon path={mdiCheckCircle} className="size-16 text-success" />
          <h2>Thank You!</h2>
          <p>
            Thank you for submitting your bank account information. Someone will contact you soon.
          </p>
          <Button onClick={() => navigate(routes.HOME)} className="mt-4">
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <img
            src="/logo.svg"
            alt="HaulPay"
            className={styles.logo}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <h1>Update Bank Account</h1>
        </div>

        <div className={styles.content}>
          <div className={styles.iconWrapper}>
            <Icon path={mdiBankOutline} className="size-12 text-primary" />
          </div>

          <p className={styles.description}>
            Please link your bank account to{" "}
            {isPaymentProfile ? "receive payments" : "complete your application"}.
          </p>

          <div className={styles.plaidSection}>
            <PlaidLinkComponent
              label="Link Bank Account"
              btnClass="!h-14 !text-lg !bg-primary hover:!bg-primary/90"
              className="w-full"
              loading={isUpdating}
              onPlaidSuccess={handlePlaidSuccess}
              onPlaidFailed={handlePlaidFailed}
            />
          </div>

          <div className={styles.divider}>
            <span>Or</span>
          </div>

          {uploadUrl && (
            <div className={styles.uploadSection}>
              <ChunkUploadFile
                triggerVariant="button"
                label={isPaymentProfile ? "Upload Voided Check or NOA" : "Upload Voided Check"}
                urlCallback={uploadUrl}
                onUploadFinished={handleUploadFinished}
                categories={NEW_CARRIER_CATEGORY}
                defaultCategory={NEW_CARRIER_CATEGORY.find((c) => c.value === "voided_check")}
                disableCategorySelection
                className={styles.uploadButton}
              />
            </div>
          )}

          <p className={styles.warningText}>
            Please enter your bank information for us to accelerate your application.
          </p>

          {isUpdating && <p className={styles.updatingText}>Updating your bank account...</p>}
        </div>

        <div className={styles.securityNote}>
          <p>
            Your bank information is securely processed through Plaid. We never store your login
            credentials.
          </p>
        </div>
      </div>
    </div>
  );
}
